/**
 * @fileoverview My Page summary panel.
 * @description The top of My Page: four KPI cards (targets assigned, in progress, pending and
 * achievement) and the Overall Achievement Progress bar with its status legend, all read from the
 * backend's My Page summary. Each target is counted once under its display status, so the status
 * counts add up to the total; In Progress is On Track plus At Risk, Pending is Overdue, and
 * Achievement is the share of targets that are Completed.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import SegmentedBar from '@/features/sales/components/SegmentedBar'
import KpiStrip from '@/shared/components/molecules/KpiStrip'

import type { MyPageSummary } from '@/features/sales/types/my-page.types'

interface MyPageSummaryPanelProps {
  summary: MyPageSummary
}

interface ProgressSegment {
  status: 'COMPLETED' | 'AWAITING_PAYMENT' | 'ON_TRACK' | 'AT_RISK' | 'OVERDUE' | 'CLOSED'
  testId: string
  color: string
  value: number
}

/**
 * @description The progress bar's segments, in display order, each with its legend colour.
 * @param {MyPageSummary} summary - The KPI counts.
 * @returns {ProgressSegment[]} One segment per display status.
 */
function progressSegments(summary: MyPageSummary): ProgressSegment[] {
  return [
    { status: 'COMPLETED', testId: 'completed', color: 'bg-teal', value: summary.completed },
    {
      status: 'AWAITING_PAYMENT',
      testId: 'awaiting-payment',
      color: 'bg-info',
      value: summary.awaitingPayment,
    },
    { status: 'ON_TRACK', testId: 'on-track', color: 'bg-track', value: summary.onTrack },
    { status: 'AT_RISK', testId: 'at-risk', color: 'bg-status-warning', value: summary.atRisk },
    { status: 'OVERDUE', testId: 'overdue', color: 'bg-danger', value: summary.overdue },
    { status: 'CLOSED', testId: 'closed', color: 'bg-table-header', value: summary.closed },
  ]
}

interface ProgressCardProps {
  segments: ProgressSegment[]
  completed: number
  total: number
}

/**
 * @component
 * @param {ProgressCardProps} props - The segments and the headline counts.
 * @param {ProgressSegment[]} props.segments - The progress bar's segments with their legend colours.
 * @param {number} props.completed - Targets that are Completed.
 * @param {number} props.total - All targets.
 */
function ProgressCard({ segments, completed, total }: ProgressCardProps) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-3.5 rounded-[10px] border border-divider bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-[15px] font-bold text-ink" data-testid="my-work-progress-title">
          {t('sales.myWork.progressTitle')}
        </h2>
        <span className="flex-1" />
        <span className="text-[12.5px] font-medium text-slate" data-testid="my-work-progress-line">
          {t('sales.myWork.progressLine', { completed, total })}
        </span>
      </div>
      <SegmentedBar
        height="md"
        corners="soft"
        segments={segments.map(({ status, color, value }) => ({
          label: t(`sales.myWork.displayStatuses.${status}`),
          value,
          color,
        }))}
      />
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-2 lg:flex-nowrap">
        {segments.map(({ status, testId, color, value }) => (
          <div
            key={status}
            className="flex items-center gap-2 whitespace-nowrap"
            data-testid={`my-work-legend-${testId}`}
          >
            <span className={`size-[9px] rounded-full ${color}`} />
            <span className="text-[12.5px] font-medium text-slate">
              {t(`sales.myWork.displayStatuses.${status}`)}
            </span>
            <span
              className="text-[12.5px] font-bold text-ink tabular-nums"
              data-testid={`my-work-legend-${testId}-value`}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * @component
 * @param {MyPageSummaryPanelProps} props - The summary to show.
 * @param {MyPageSummary} props.summary - The KPI counts and the financial year they cover.
 */
function MyPageSummaryPanel({ summary }: MyPageSummaryPanelProps) {
  const { t } = useTranslation()
  const { total, completed, onTrack, atRisk } = summary
  const percentComplete = total ? Math.round((completed / total) * 100) : 0
  const segments = progressSegments(summary)

  return (
    <>
      <KpiStrip
        tiles={[
          {
            label: t('sales.myWork.kpi.assigned'),
            sublabel: t('sales.myWork.kpi.assignedSublabel', { count: summary.awaitingPayment }),
            value: total,
            icon: 'userCheck',
            iconTone: 'navy',
            testId: 'my-work-kpi-assigned',
          },
          {
            label: t('sales.myWork.kpi.inProgress'),
            sublabel: t('sales.myWork.kpi.inProgressSublabel', { onTrack, atRisk }),
            value: onTrack + atRisk,
            icon: 'trend',
            iconTone: 'blue',
            testId: 'my-work-kpi-in-progress',
          },
          {
            label: t('sales.myWork.kpi.pending'),
            sublabel: t('sales.myWork.kpi.pendingSublabel'),
            value: summary.overdue,
            icon: 'hourglass',
            iconTone: 'amber',
            testId: 'my-work-kpi-pending',
            tone: summary.overdue > 0 ? 'danger' : undefined,
          },
          {
            label: t('sales.myWork.kpi.achievement'),
            sublabel: t('sales.myWork.kpi.achievementSublabel', { completed, total }),
            value: `${percentComplete}%`,
            icon: 'check',
            iconTone: 'green',
            testId: 'my-work-kpi-achievement',
            tone: 'success',
          },
        ]}
      />

      <ProgressCard segments={segments} completed={completed} total={total} />
    </>
  )
}

export default MyPageSummaryPanel
